import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { fetchWithAuth } from '@/lib/api';
import toast from 'react-hot-toast';

interface Mission {
  id: string;
  title: string;
  description?: string;
  type: 'daily' | 'weekly' | 'one-time';
  actionType: string;
  targetCount: number;
  rewardPoints: number;
  progress: number;
  completed: boolean;
  claimed: boolean;
}

export function useMissions() {
  const { userId, isAuthenticated } = useAuthStore();
  const [missions, setMissions] = useState<Mission[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchMissions = useCallback(async () => {
    if (!isAuthenticated || !userId) {
      setMissions([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const res = await fetchWithAuth('/api/missions/user');
      if (!res.ok) throw new Error();

      const json = await res.json();
      setMissions(json.data.map((m: Record<string, unknown>) => ({
        id: m.id,
        title: m.title,
        description: m.description || '',
        type: m.type,
        actionType: m.actionType,
        targetCount: m.targetCount,
        rewardPoints: m.rewardPoints,
        progress: m.progress || 0,
        completed: m.completed || false,
        claimed: !!m.claimed,
      })));
    } catch {
      // 미로그인 또는 API 실패 시 빈 목록
      setMissions([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId, isAuthenticated]);

  useEffect(() => {
    fetchMissions();
  }, [fetchMissions]);

  const claimReward = async (missionId: string) => {
    try {
      const res = await fetchWithAuth(`/api/missions/${missionId}/complete`, {
        method: 'POST',
      });
      if (!res.ok) {
        const json = await res.json();
        toast.error(json.message || '보상 수령에 실패했습니다.');
        return false;
      }

      const json = await res.json();
      toast.success(json.data.message || '보상을 수령했습니다!');

      // 로컬 상태 업데이트
      setMissions(prev =>
        prev.map(m => m.id === missionId ? { ...m, claimed: true } : m)
      );

      return true;
    } catch {
      toast.error('보상 수령에 실패했습니다.');
      return false;
    }
  };

  const getCompletedMissions = () => {
    return missions.filter(m => m.completed && !m.claimed);
  };

  const getTotalProgress = () => {
    const total = missions.reduce((sum, m) => sum + m.targetCount, 0);
    if (total === 0) return 0;
    const current = missions.reduce((sum, m) => sum + m.progress, 0);
    return Math.round((current / total) * 100);
  };

  return {
    missions,
    isLoading,
    claimReward,
    getCompletedMissions,
    getTotalProgress,
    refresh: fetchMissions,
  };
}
